﻿import { Dispatch, SetStateAction, useEffect } from 'react';

import { GMetrixDialog } from 'components/GMetrixDialog/GMetrixDialog';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { GMetrixLoader } from 'components/GMetrixLoader/GMetrixLoader';
import { GMetrixTable } from 'components/GMetrixTable/GMetrixTable';
import { GMetrixInput } from 'components/GMetrixInput/GMetrixInput';
import { Badge, Card, Col, Form, Row } from 'react-bootstrap';
import { Api } from 'pages/orders/fulfillment_manager/Api';
import { mapToRecordObject, removeUndefined } from 'utils';
import { FormattedMessage, useIntl } from 'react-intl';
import { Deliverable, Fulfillment } from 'types';
import { useSuperState } from 'hooks';
import { useApp } from 'app';

import 'pages/orders/fulfillment_manager/components/FulfillmentView/styles/FulfillmentView.scss';

type FulfillmentViewProps = {
    fulfillment?: Fulfillment;
    modalOpen: boolean;
    onAddItemClick: () => void;
    setCurrentFulfillment: Dispatch<SetStateAction<Fulfillment | undefined>>;
    setModalOpen: Dispatch<SetStateAction<boolean>>;
    shouldBeLoading: boolean;
    updateFulfillment: (updatedFulfillment: Fulfillment) => void;
}

type DeliverableTableRow = {
    CategoryName: string;
    Id: number;
    Name: string;
    Selected: string;
    SKU: string;
    VendorName: string;
}

const FulfillmentView = (props: FulfillmentViewProps) => {

    const { fulfillment, modalOpen, onAddItemClick, setCurrentFulfillment, setModalOpen, shouldBeLoading, updateFulfillment } = props;

    const [state, setState] = useSuperState({
        loading: false,
        newDescription: '',
        newFulfillmentCode: '',
        savedField: null as 'code' | 'description' | null,
        savingField: null as 'code' | 'description' | null,
        selectedDeliverables: {} as Record<number, (Deliverable | undefined)>
    });

    const { loading, newDescription, newFulfillmentCode, savedField, savingField, selectedDeliverables } = state;

    const { userId } = useApp();
    const { formatMessage } = useIntl();

    useEffect(() => {

        if (fulfillment) {

            setState({ newDescription: fulfillment.Description, newFulfillmentCode: fulfillment.Code });
            if (!fulfillment.Deliverables) {

                setState({ loading: true });
                const loadDeliverables = async () => {

                    try {

                        const deliverables = await Api.GetFulfillmentDeliverables(fulfillment.ExternalId, userId);
                        updateFulfillment({
                            ...fulfillment,
                            Deliverables: deliverables
                        });
                        setState({ loading: false });

                    } catch {

                        setState({ loading: false });

                    }

                };
                loadDeliverables();

            }

        }

    }, [fulfillment]);

    useEffect(() => setState({ loading: shouldBeLoading }), [shouldBeLoading]);

    const saveCode = async () => {

        if (fulfillment) {

            setState({ savingField: 'code' });
            try {

                const res = await Api.UpdateFulfillment(
                    fulfillment.ExternalId,
                    newFulfillmentCode,
                    fulfillment.DeliverableTypeId,
                    fulfillment.Description,
                    userId
                );
                setState({ savingField: null });
                if (res.Successful) {

                    updateFulfillment({
                        ...fulfillment,
                        Code: newFulfillmentCode
                    });
                    setState({ savedField: 'code' });
                    setTimeout(() => setState({ savedField: null }), 2000);

                }

            } catch {

                setState({ savingField: null });

            }

        }

    };

    const saveDescription = async () => {

        if (fulfillment) {

            setState({ savingField: 'description' });
            try {

                const res = await Api.UpdateFulfillment(
                    fulfillment.ExternalId,
                    fulfillment.Code,
                    fulfillment.DeliverableTypeId,
                    newDescription,
                    userId
                );
                setState({ savingField: null });
                if (res.Successful) {

                    updateFulfillment({
                        ...fulfillment,
                        Description: newDescription
                    });
                    setState({ savedField: 'description' });
                    setTimeout(() => setState({ savedField: null }), 2000);

                }

            } catch {

                setState({ savingField: null });

            }

        }

    };

    const onDeliverableCheckboxClick = (deliverable: Deliverable) => setState({
        selectedDeliverables: {
            ...selectedDeliverables,
            [deliverable.Id]: selectedDeliverables[deliverable.Id] ? undefined : deliverable
        }
    });

    const onHeaderCheckboxClick = () => {

        if (fulfillment?.Deliverables) {

            if (fulfillment.Deliverables.every(deliverable => selectedDeliverables[deliverable.Id])) {

                setState({ selectedDeliverables: {} });

            } else {

                setState({ selectedDeliverables: mapToRecordObject(fulfillment.Deliverables, 'Id') });

            }

        }

    };

    const getHeaderCheckboxState = () => {

        let checked = true;
        for (const deliverable of fulfillment?.Deliverables || []) {

            if (selectedDeliverables[deliverable.Id] === undefined) {

                checked = false;
                break;

            }

        }
        return checked;

    };

    const onRemoveSelectedClick = async () => {

        if (fulfillment) {

            setState({ loading: true });
            const internalIds = removeUndefined(
                Object.values(selectedDeliverables)
            ).map(deliverable => deliverable.Id);
            try {

                const successObj = await Api.RemoveDeliverablesFromFulfillment(fulfillment.ExternalId, userId, internalIds);
                const deliverableIdsRemoved = successObj.DataExtra || [];
                const currentFulfillmentDeliverables = fulfillment.Deliverables || [];
                const newDeliverables = currentFulfillmentDeliverables.filter(deliverable => !deliverableIdsRemoved.includes(deliverable.Id));
                updateFulfillment({
                    ...fulfillment,
                    Count: newDeliverables.length,
                    Deliverables: newDeliverables
                });
                setState({ loading: false, selectedDeliverables: {} });

            } catch {

                setState({ loading: false });

            }

        }

    };

    const selectedCount = removeUndefined(Object.values(selectedDeliverables)).length;
    const itemCount = fulfillment?.Deliverables?.length || 0;

    const tableData: DeliverableTableRow[] = (fulfillment?.Deliverables || []).map(d => ({
        CategoryName: d.Category.Name,
        Id: d.Id,
        Name: d.Name,
        Selected: selectedDeliverables[d.Id] ? '✓' : '',
        SKU: d.SKU,
        VendorName: d.VendorName
    }));

    const columns = [
        { field: 'Selected' as keyof DeliverableTableRow, sortable: false, title: '' },
        { field: 'Id' as keyof DeliverableTableRow, sortable: true, title: formatMessage({ id: 'id.e9650a61', defaultMessage: 'ID' }) },
        { field: 'Name' as keyof DeliverableTableRow, sortable: true, title: formatMessage({ id: 'name.5458f379', defaultMessage: 'Name' }) },
        { field: 'CategoryName' as keyof DeliverableTableRow, sortable: true, title: formatMessage({ id: 'category.f324da36', defaultMessage: 'Category' }) },
        { field: 'SKU' as keyof DeliverableTableRow, sortable: true, title: formatMessage({ id: 'sku.072d52e0', defaultMessage: 'SKU' }) },
        { field: 'VendorName' as keyof DeliverableTableRow, sortable: true, title: formatMessage({ id: 'vendor.b7db4ace', defaultMessage: 'Vendor' }) }
    ];

    return (
        <GMetrixDialog
            dialogLabel={(
                <div className="d-flex align-items-center gap-2">
                    <i className="fa-solid fa-box-open" />
                    <span>
                        <FormattedMessage defaultMessage="Edit Fulfillment Code" id="edit_fulfillment_code.f9b29535" />
                    </span>
                </div>
            )}
            onCancel={() => {

                setModalOpen(false);
                setTimeout(() => setCurrentFulfillment(undefined), 200);

            }}
            actionButtonClick={() => setModalOpen(false)}
            actionButtonDisabled={false}
            actionButtonText={formatMessage({ id: 'close.99e01fb5', defaultMessage: 'Close' })}
            show={modalOpen}
            showCancelButton={false}
            size="lg"
        >
            <div className="fulfillment-view">
                <Card className="fulfillment-view__details-card mb-4">
                    <Card.Header className="fulfillment-view__card-header">
                        <h6 className="mb-0 fw-bold d-flex align-items-center">
                            <i className="fa-solid fa-circle-info me-2 text-primary" />
                            {<FormattedMessage defaultMessage="Details" id="details.7799b923" /> || 'Details'}
                        </h6>
                    </Card.Header>
                    <Card.Body>
                        <Row className="g-3">
                            <Col md={6}>
                                <GMetrixInput
                                    label={formatMessage({ id: 'fulfillment_code.de107b2d', defaultMessage: 'Fulfillment Code' })}
                                    maxLength={50}
                                    onChange={e => setState({ newFulfillmentCode: e.target.value })}
                                    value={newFulfillmentCode}
                                />
                                <div className="mt-2">
                                    <GMetrixButton
                                        disabled={savingField === 'code' || savedField === 'code' || newFulfillmentCode === fulfillment?.Code}
                                        iconClass={savedField === 'code' ? 'fa-solid fa-check' : 'fa-solid fa-floppy-disk'}
                                        onClick={saveCode}
                                        size="sm"
                                        variant={savedField === 'code' ? 'outline-success' : 'outline-primary'}
                                    >
                                        {savingField === 'code'
                                            ? <GMetrixLoader size="small" />
                                            : savedField === 'code' ? 'Saved!' : <FormattedMessage defaultMessage="Save" id="save.d44082ae" /> || 'Save'}
                                    </GMetrixButton>
                                </div>
                            </Col>
                            <Col md={6}>
                                <GMetrixInput
                                    readOnly
                                    label={formatMessage({ id: 'fulfillment_type.d5e0090b', defaultMessage: 'Fulfillment Type' })}
                                    value={fulfillment?.DeliverableTypeName || ''}
                                />
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col>
                                <GMetrixInput
                                    as="textarea"
                                    label={formatMessage({ id: 'description.e1c24171', defaultMessage: 'Description', description: 'Formulario' })}
                                    maxLength={250}
                                    onChange={e => setState({ newDescription: e.target.value })}
                                    rows={3}
                                    value={newDescription}
                                />
                                <div className="mt-2">
                                    <GMetrixButton
                                        disabled={savingField === 'description' || savedField === 'description' || newDescription === fulfillment?.Description}
                                        iconClass={savedField === 'description' ? 'fa-solid fa-check' : 'fa-solid fa-floppy-disk'}
                                        onClick={saveDescription}
                                        size="sm"
                                        variant={savedField === 'description' ? 'outline-success' : 'outline-primary'}
                                    >
                                        {savingField === 'description'
                                            ? <GMetrixLoader size="small" />
                                            : savedField === 'description' ? 'Saved!' : <FormattedMessage defaultMessage="Save" id="save.d44082ae" /> || 'Save'}
                                    </GMetrixButton>
                                </div>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                <div className="fulfillment-view__items-header d-flex align-items-center justify-content-between mb-3">
                    <h6 className="mb-0 fw-bold d-flex align-items-center">
                        <i className="fa-solid fa-list me-2" />
                        <FormattedMessage defaultMessage="Items" id="items.8e128cc2" />
                        <Badge pill bg="secondary" className="ms-2">
                            {itemCount}
                        </Badge>
                    </h6>
                    <Form.Check
                        checked={getHeaderCheckboxState() && itemCount > 0}
                        className="fulfillment-view__select-all"
                        disabled={itemCount === 0}
                        label={formatMessage({ id: 'select_all.dd487b25', defaultMessage: 'Select All' })}
                        onChange={onHeaderCheckboxClick}
                        type="checkbox"
                    />
                </div>

                <GMetrixTable<DeliverableTableRow>
                    allowExport
                    onRowClick={row => {

                        const deliverable = (fulfillment?.Deliverables || []).find(d => d.Id === row.Id);
                        if (deliverable) {

                            onDeliverableCheckboxClick(deliverable);

                        }

                    }}
                    columns={columns}
                    data={tableData}
                    loading={loading}
                    searchPlaceholder={formatMessage({ id: 'search.0ab06b2c', defaultMessage: 'Search' }) || 'Search deliverables...'}
                />

                <div className="fulfillment-view__actions d-flex justify-content-between align-items-center mt-3">
                    <GMetrixButton
                        disabled={selectedCount === 0}
                        iconClass="fa-solid fa-trash-can"
                        onClick={onRemoveSelectedClick}
                        variant="outline-danger"
                    >
                        <FormattedMessage defaultMessage="Remove Selected" id="remove_selected.8386f85d" />
                        {selectedCount > 0 && (
                            <Badge pill bg="danger" className="ms-2">
                                {selectedCount}
                            </Badge>
                        )}
                    </GMetrixButton>
                    <GMetrixButton
                        iconClass="fa-solid fa-plus"
                        onClick={onAddItemClick}
                        variant="primary"
                    >
                        <FormattedMessage defaultMessage="Add Items" id="add_items.7fea448c" />
                    </GMetrixButton>
                </div>
            </div>
        </GMetrixDialog>

    );

};

export { FulfillmentView };
